<script setup>
import {
    Chart as ChartJS,
    Title,
    Tooltip,
    Legend,
    BarElement,
    CategoryScale,
    LinearScale
} from 'chart.js'
import annotationPlugin from 'chartjs-plugin-annotation'
import { Bar } from 'vue-chartjs'
import { computed, ref, watch, onMounted } from 'vue'

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, annotationPlugin)

const props = defineProps({
    showXAxisLabel: { type: Boolean, default: true },
    labels: { type: Array, default: () => [] },
    colors: { type: Array, default: () => [] },
    data: { type: Array, default: () => [] },
    dataIds: { type: Array, default: () => [] },
    title: { type: String, default: '' },
    upperLimit: { type: Number, default: null },
    lowerLimit: { type: Number, default: null }
})

const emit = defineEmits(['onBarClick'])

const chartRef = ref(null)
const chartData = ref(getChartData())
const options = ref(getChartOptions())

watch(() => props.data, () => {
    chartData.value = getChartData()
}, { immediate: true })

function getChartData() {
    return {
        labels: props.labels,
        datasets: [
            {
                label: props.title,
                backgroundColor: props.colors,
                data: props.data.map(item => item.value),
                maxBarThickness: 50
            }
        ]
    }
}

function getChartOptions() {
    const annotations = []

    if (props.lowerLimit !== null) {
        annotations.push({
            type: 'line',
            yMin: props.lowerLimit,
            yMax: props.lowerLimit,
            borderColor: '#c57f7f',
            borderWidth: 1
        })
    }

    if (props.upperLimit !== null) {
        annotations.push({
            type: 'line',
            yMin: props.upperLimit,
            yMax: props.upperLimit,
            borderColor: '#f56a6a',
            borderWidth: 1
        })
    }

    return {
        responsive: true,
        maintainAspectRatio: false,
        onClick: handleClick,
        scales: {
            x: {
                ticks: {
                    display: props.showXAxisLabel
                }
            },
            y: {
                beginAtZero: true
            }
        },
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                enabled: true
            },
            annotation: {
                annotations
            }
        },
        hover: {
            onHover: (e, elements) => {
                e.native.target.style.cursor = elements.length ? 'pointer' : 'default'
            }
        }
    }
}

function handleClick(event, elements) {
    if (elements.length > 0) {
        const index = elements[0].index
        emit('onBarClick', index)
        updateColorSet(index)
    }
}

function updateColorSet(index) {
    const dataset = chartData.value.datasets[0]
    const colors = [...props.colors]
    colors[index] = '#f5d982'
    dataset.backgroundColor = colors
}

function setSelected(index) {
    const chart = chartRef.value?.chart
    if (!chart) return

    // Clone the dataset completely and update the color
    const newColors = props.colors.slice()
    newColors[index] = '#f5d982'

    chartData.value = {
        ...chartData.value,
        datasets: [
            {
                ...chartData.value.datasets[0],
                backgroundColor: newColors
            }
        ]
    }

    chart.update()
}

function getChartAsImage() {
    return chartRef.value?.chart.toBase64Image()
}

defineExpose({
    setSelected,
    getChartAsImage
})

</script>

<template>
    <div class="nopads debug" style="height: 100%;"> <!-- adjust height here -->
        <Bar
            ref="chartRef"
            :data="chartData"
            :options="options"
        />
    </div>
</template>
